import {
	Button,
	FormControlLabel,
	Grid2,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../components/modals/error-modal";
import MesfModal from "../../../../components/modals/modal.mesf";
import { ButtonWithLoading } from "../../../../components/shared/buttons/button-with-loading";
import type { AppRelease } from "../models/AppRelease";
import { compareVersions, isValidVersion } from "../models/versions";
import { updateAppRelease } from "../repositories/AppReleasesRepository";

type FormValues = {
	MinSupportedVersion: string;
	ReleaseNotes: string;
	IsActive: boolean;
};

type Props = {
	release: AppRelease | null;
	onHide: (saved: boolean) => void;
};

export const EditReleaseModal = ({ release, onHide }: Props) => {
	const queryClient = useQueryClient();
	const [error, setError] = useState("");

	const { control, reset, handleSubmit } = useForm<FormValues>({
		defaultValues: {
			MinSupportedVersion: "",
			ReleaseNotes: "",
			IsActive: false,
		},
	});

	useEffect(() => {
		if (release) {
			reset({
				MinSupportedVersion: release.MinSupportedVersion ?? "",
				ReleaseNotes: release.ReleaseNotes ?? "",
				IsActive: !!release.IsActive,
			});
		}
	}, [release, reset]);

	const updateMutation = useMutation(updateAppRelease, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["appReleases"]);
			onHide(true);
		},
		onError: (mutationError: Error) => {
			setError(mutationError.message);
		},
	});

	const isSaving = updateMutation.isLoading;

	const onSubmit: SubmitHandler<FormValues> = (data) => {
		if (!release) {
			return;
		}

		updateMutation.mutate({
			AppReleaseId: release.AppReleaseId,
			AppCode: release.AppCode,
			Platform: release.Platform,
			Version: release.Version,
			Build: release.Build,
			MinSupportedVersion: data.MinSupportedVersion.trim() || null,
			ReleaseNotes: data.ReleaseNotes.trim() || null,
			IsActive: data.IsActive,
		});
	};

	return (
		<>
			<MesfModal
				title="EDIT RELEASE"
				open={release !== null}
				handleClose={() => !isSaving && onHide(false)}
				maxWidth="sm"
				id="edit-app-release-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "10px 30px" }}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<Typography variant="subtitle1">
									Version <b>{release?.Version}</b> - build{" "}
									<b>{release?.Build}</b>
								</Typography>
								{release?.FileName ? (
									<Typography variant="body2" color="textSecondary">
										{release.FileName}
									</Typography>
								) : null}
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 6 }}>
								<Controller
									name="MinSupportedVersion"
									control={control}
									rules={{
										validate: (value) => {
											const trimmed = value.trim();
											if (trimmed === "") {
												return true;
											}
											if (!isValidVersion(trimmed)) {
												return "Use the format 1.2.3";
											}
											if (
												release &&
												compareVersions(trimmed, release.Version) > 0
											) {
												return "Cannot be higher than the version";
											}
											return true;
										},
									}}
									render={({ field, fieldState: { error: fieldError } }) => (
										<TextField
											{...field}
											label="Min supported version"
											fullWidth
											margin="dense"
											autoComplete="off"
											disabled={isSaving}
											error={!!fieldError}
											helperText={
												fieldError?.message ??
												"Devices below this version must update"
											}
										/>
									)}
								/>
							</Grid2>
							<Grid2
								size={{ xs: 12, sm: 6 }}
								sx={{ display: "flex", alignItems: "center" }}
							>
								<Controller
									name="IsActive"
									control={control}
									render={({ field: { value, onChange } }) => (
										<FormControlLabel
											control={
												<Switch
													checked={value}
													onChange={(event) => onChange(event.target.checked)}
													disabled={isSaving}
												/>
											}
											label="Active"
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="ReleaseNotes"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label="Release notes"
											fullWidth
											multiline
											rows={4}
											margin="dense"
											autoComplete="off"
											disabled={isSaving}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
									disabled={isSaving}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSaving}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In App Releases"
			/>
		</>
	);
};
